import { useMemo, useState } from "react";
import { characters } from "@/mocks/characters";
import { works } from "@/mocks/works";
import CharacterCard from "@/components/feature/CharacterCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useJsonLd } from "@/hooks/useJsonLd";

type WorkFilter = "all" | string;
type SortKey = "rarity" | "name";

export default function Characters() {
  usePageMeta(
    "女神漫画角色图鉴｜角色大全·分类与介绍",
    "女神漫画角色图鉴页按作品与设定分类整理高人气女神角色，支持筛选与搜索，点开即可查看每位女神角色的详细背景、性格特点与名场面介绍。"
  );

  const [query, setQuery] = useState("");
  const [workFilter, setWorkFilter] = useState<WorkFilter>("all");
  const [sort, setSort] = useState<SortKey>("rarity");

  const filtered = useMemo(() => {
    let list = [...characters];
    if (workFilter !== "all") {
      list = list.filter((c) => c.workId === workFilter);
    }
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.nameEn.toLowerCase().includes(q) ||
          c.epithet.toLowerCase().includes(q) ||
          c.tags.some((t) => t.toLowerCase().includes(q))
      );
    }
    if (sort === "rarity") {
      list.sort((a, b) => b.rarity - a.rarity);
    } else {
      list.sort((a, b) => a.name.localeCompare(b.name, "zh"));
    }
    return list;
  }, [query, workFilter, sort]);

  useJsonLd(
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "女神漫画角色图鉴",
      description: "按作品与设定分类整理的高人气女神漫画角色大全。",
      mainEntity: {
        "@type": "ItemList",
        itemListElement: filtered.slice(0, 12).map((c, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: c.name,
          url: `https://ywgvp87.cn/character/${c.id}`,
        })),
      },
    },
    "characters"
  );

  return (
    <>
      <section className="relative flex min-h-[260px] md:min-h-[300px] w-full items-center overflow-hidden bg-foreground-950">
        <img
          src="https://readdy.ai/api/search-image?query=collection%20of%20ethereal%20goddess%20figures%20silhouettes%20across%20a%20glowing%20constellation%20sky%2C%20wide%20fantasy%20anime%20banner%2C%20warm%20amber%20crimson%20and%20ivory%20palette%2C%20soft%20luminous%20particles%2C%20majestic%20serene%20atmosphere&width=1600&height=600&seq=chars-banner-2026&orientation=landscape"
          alt="女神角色图鉴横幅背景"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/45 to-black/70"></div>
        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pt-16 pb-8 text-white md:pt-24">
          <nav aria-label="面包屑" className="text-xs text-white/70">
            <span className="hover:text-white"><a href="/" rel="nofollow">首页</a></span>
            <span className="mx-2">/</span>
            <span>角色图鉴</span>
          </nav>
          <h1 className="mt-4 font-heading text-3xl font-black md:text-5xl">女神漫画角色图鉴</h1>
          <p className="mt-4 max-w-2xl text-sm text-white/80 md:text-base">
            按作品与设定分类整理的高人气女神角色大全。使用下方筛选与搜索，快速找到你感兴趣的那一位。
          </p>
        </div>
      </section>

      <section className="bg-background-50 py-10 md:py-14">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setWorkFilter("all")}
                className={`rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  workFilter === "all"
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                全部
              </button>
              {works.map((w) => (
                <button
                  key={w.id}
                  type="button"
                  onClick={() => setWorkFilter(w.id)}
                  className={`rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                    workFilter === w.id
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {w.title}
                </button>
              ))}
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative">
                <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-foreground-400" aria-hidden="true"></i>
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="搜索角色 / 称号 / 标签"
                  aria-label="搜索角色"
                  className="w-full rounded-full border border-background-300 bg-background-50 py-2.5 pl-10 pr-4 text-sm outline-none transition-colors focus:border-primary-400 sm:w-64"
                />
              </div>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as SortKey)}
                aria-label="排序方式"
                className="rounded-full border border-background-300 bg-background-50 px-4 py-2.5 text-sm outline-none transition-colors focus:border-primary-400"
              >
                <option value="rarity">按人气排序</option>
                <option value="name">按名称排序</option>
              </select>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between text-sm text-foreground-500">
            <span>
              共 <strong className="text-primary-600">{filtered.length}</strong> 位女神角色
              {workFilter !== "all" && <> · {works.find((w) => w.id === workFilter)?.title}</>}
            </span>
            <span className="hidden sm:block">点击卡片查看详情</span>
          </div>

          {filtered.length > 0 ? (
            <div className="mt-6 grid grid-cols-2 gap-4 md:gap-6 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {filtered.map((c) => (
                <CharacterCard key={c.id} c={c} />
              ))}
            </div>
          ) : (
            <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 py-16 text-center">
              <i className="ri-search-eye-line text-4xl text-background-300" aria-hidden="true"></i>
              <p className="mt-4 font-medium text-foreground-700">没有找到匹配的角色</p>
              <p className="mt-1 text-sm text-foreground-500">换个关键词或清除筛选条件试试</p>
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  setWorkFilter("all");
                }}
                className="mt-5 rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap hover:bg-primary-600"
              >
                清除筛选
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
}